import { Box, Paper } from "@mui/material"
import { DownloadOutlined, MailOutlined, PrintOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useEffect, useMemo, useRef } from "react"

import { Button } from "@/components/common/Button"
import { PageHeader } from "@/components/PageHeader/PageHeader"
import type { ProductSortField } from "@/graphql/codegen/graphql"
import { ComputedProductStatus, SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { FilterDrawer } from "@/screens/Products/components/filters/FilterDrawer"
import ProductListFilterChips from "@/screens/Products/components/filters/ProductListFilterChips"
import ProductsHeaderActions from "@/screens/Products/components/headerActions/ProductsHeaderActions"
import ProductListTable from "@/screens/Products/components/tables/productListTable"
import { useProductListFiltering } from "@/screens/Products/hooks/useProductListFiltering"
import type { ProductListSearchFilters } from "@/screens/Products/stores/useProductListSearchStore"
import { useProductListSearchStore } from "@/screens/Products/stores/useProductListSearchStore"
import type { ProductsSortableAccessorKey } from "@/screens/Products/utils/productsTableConfig"
import {
  accessorKeyToProductsSortField,
  productsSortFieldToAccessoryKey,
} from "@/screens/Products/utils/productsTableConfig"

const ProductListContainer: FC = () => {
  const navigate = useNavigate()
  const { query, field, order, computedProductStatuses, companyIds } = useSearch({ from: "/app/products/" })

  const { filterDrawerOpen, setFilterDrawerOpen } = useProductListFiltering()

  const data = useProductListSearchStore(({ data }) => data)
  const fetching = useProductListSearchStore(({ fetching }) => fetching)
  const error = useProductListSearchStore(({ error }) => error)
  const executeSearch = useProductListSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useProductListSearchStore(({ loadMore }) => loadMore)
  const clearStore = useProductListSearchStore(({ clearStore }) => clearStore)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    navigate({
      search: {
        computedProductStatuses: [ComputedProductStatus.Active],
        field,
        order,
      },
      to: "/app/products",
      replace: true,
    })
  }, [field, navigate, order])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  useEffect(() => {
    const searchFilters: ProductListSearchFilters = {
      query,
      field,
      order,
      computedProductStatuses,
      companyIds,
    }
    executeSearch(queryClient, searchFilters)
  }, [query, field, order, executeSearch, computedProductStatuses, companyIds])

  const [throttledLoadMore] = useThrottle(() => {
    const searchFilters: ProductListSearchFilters = {
      query,
      field,
      order,
      computedProductStatuses,
      companyIds,
    }
    loadMore(queryClient, searchFilters)
  }, 200)

  const scrollHandler = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const sorting = useMemo(() => {
    if (!field) return []

    const tableSortField = productsSortFieldToAccessoryKey.get(field as ProductSortField)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  const handleSortingChange = (updaterFn: (state: SortingState) => SortingState) => {
    const newSorting = updaterFn(sorting)
    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id as ProductsSortableAccessorKey
      const field = accessorKeyToProductsSortField.get(key) as ProductSortField
      const params = {
        query,
        computedProductStatuses,
        companyIds,
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }

      navigate({
        search: params,
        to: "/app/products",
        replace: true,
      })
    }
  }

  const handleToggleProductStatus = () => {
    const searchFilters: ProductListSearchFilters = {
      query,
      field,
      order,
      computedProductStatuses,
      companyIds,
    }

    executeSearch(queryClient, searchFilters, true)
  }

  return (
    <Paper elevation={0} className='flex min-h-0 grow flex-col gap-4 overflow-hidden bg-white'>
      <PageHeader title='Products'>
        <div className='space-x-2'>
          <Button size='small' startIcon={<MailOutlined />} appearance='text'>
            Email
          </Button>
          <Button size='small' startIcon={<PrintOutlined />} appearance='text'>
            Print
          </Button>
          <Button size='small' startIcon={<DownloadOutlined />} appearance='text'>
            Export
          </Button>
        </div>
      </PageHeader>

      <ProductsHeaderActions setFilterDrawerOpen={setFilterDrawerOpen} />

      <FilterDrawer showFilterDrawer={filterDrawerOpen} toggleDrawer={setFilterDrawerOpen} />

      <Box data-testid='product-menu-filter-chips' className='flex flex-wrap items-center gap-4'>
        <ProductListFilterChips />
      </Box>

      {/* Table Section */}
      <Box className='flex flex-1 flex-col overflow-y-auto'>
        <ProductListTable
          data={data}
          onToggleProductStatus={handleToggleProductStatus}
          tableContainerRef={tableContainerRef}
          scrollHandler={scrollHandler}
          handleSortingChange={handleSortingChange}
          sorting={sorting}
          fetching={fetching}
          error={error}
        />
      </Box>
    </Paper>
  )
}

export default ProductListContainer
